import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { STATUSES, SOURCES } from './sources';

/**
 * Module 6 — filter bar.
 *
 * Two filters:
 *  - status (chips, multi-select-like single value: all / open / in_progress / done / cancelled)
 *  - source_activity (dropdown — multi disabled, single value: all or one)
 *
 * Props:
 *  - status: 'all' | one of action_log.status
 *  - onStatusChange: (id) => void
 *  - source: 'all' | one of action_log.source_activity
 *  - onSourceChange: (id) => void
 *  - counts: optional { [statusId]: count } to render numbers on chips
 */
const ActionLogFilters = ({ status, onStatusChange, source, onSourceChange, counts = {} }) => {
  const { t } = useTranslation();

  const statusChips = [
    { id: 'all', label: t('tcn.m6.filter.all', 'All'), color: TCN.text },
    ...STATUSES.map(s => ({ id: s.id, label: t(`tcn.m6.status.${s.id}`, s.label), color: s.color })),
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {statusChips.map((s) => {
          const active = s.id === status;
          const count = counts[s.id];
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onStatusChange?.(s.id)}
              style={{
                background: active ? `${s.color}22` : 'transparent',
                color: active ? s.color : TCN.textDim,
                border: `1px solid ${active ? `${s.color}55` : TCN.border}`,
                padding: '6px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: TCN.fontSans,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              {s.label}
              {count != null && (
                <span
                  style={{
                    fontSize: 10,
                    color: TCN.textMute,
                    fontWeight: 700,
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="filter" size={14} color={TCN.textMute} />
        <select
          value={source}
          onChange={(e) => onSourceChange?.(e.target.value)}
          style={{
            background: TCN.surface2,
            color: TCN.text,
            border: `1px solid ${TCN.border}`,
            padding: '6px 10px',
            borderRadius: 8,
            // FRM-001/TCH-001 — 16px : pas de zoom iOS à l'ouverture du select.
            fontSize: 16,
            fontFamily: TCN.fontSans,
            cursor: 'pointer',
          }}
        >
          <option value="all">{t('tcn.m6.filter.allSources', 'All sources')}</option>
          {SOURCES.map((s) => (
            <option key={s.id} value={s.id}>
              {t(`tcn.m6.source.${s.id}`, s.label)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default ActionLogFilters;
